import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Button, { ButtonLink } from "@/components/ui/Button";
import { Field, Textarea } from "@/components/ui/Field";
import PageBlockEditor, {
  rowFor,
  type BlockRow,
} from "@/components/events/PageBlockEditor";
import CityPage, { slotFor, type PageSlot } from "@/components/events/CityPage";
import PagePreviewFrame from "@/components/events/PagePreviewFrame";
import { usePage } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";
import { fetchWithCsrf } from "@/utils/csrf";
import { draftPage, cacheHtml, type HtmlCache } from "@/utils/draftPage";
import type { SharedProps } from "@/types";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { EventPageBlock } from "@/interfaces/event_page";

export default function OrganizePage({
  event,
  starting_blocks,
  height_range,
  age_range,
}: {
  event: SatelliteEvent;
  starting_blocks: EventPageBlock[];
  height_range: { min: number; max: number };
  age_range: { min: number; max: number };
}) {
  const program = usePage<SharedProps>().props.program;
  const customAllowed = program.custom_event_pages_enabled;

  const [rows, setRows] = useState<BlockRow[]>(() =>
    starting_blocks.map(rowFor),
  );
  const [published, setPublished] = useState(event.page_published ?? false);
  const [customHtml, setCustomHtml] = useState(event.page?.custom_html ?? "");
  const [height, setHeight] = useState(
    event.page?.custom_html_height ?? height_range.min,
  );

  // The preview is drawn from the editor's own state on every keystroke, so it
  // never lags what has been typed. The server still has the final say: it
  // normalizes the draft, renders the markdown and names what it refuses, and
  // its answer feeds back in as the cache below.
  const [html, setHtml] = useState<HtmlCache>(() =>
    cacheHtml(new Map(), event.page ?? null),
  );
  const [problems, setProblems] = useState<string[]>([]);
  const [mobileTab, setMobileTab] = useState<"edit" | "preview">("edit");

  // Which part of the page the section being edited ends up in.
  const [highlight, setHighlight] = useState<PageSlot | null>(null);
  const paneRef = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "failed">(
    "idle",
  );

  const blocks = rows.map((row) => row.block);
  const blocksJson = JSON.stringify(blocks);

  // The only thing the server render adds is sanitized markdown, and the
  // preview endpoint allows 120 calls a minute. Asking on every keystroke in
  // every field burns that allowance on headings and captions the browser has
  // already drawn - and a rate-limited preview fails silently, so an organizer
  // would just watch their formatting stop working. So only markdown asks.
  const markdown = JSON.stringify(
    blocks.flatMap((block) =>
      block.type === "text"
        ? [block.body]
        : block.type === "faq"
          ? block.items.map((item) => item.answer)
          : [],
    ),
  );

  const preview = draftPage(blocks, html, {
    html: customHtml,
    height,
    enabled: customAllowed,
  });

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await fetchWithCsrf(`/organize/${event.id}/page/preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            page_blocks: blocksJson,
            custom_html: customHtml,
            custom_html_height: height,
          }),
        });
        if (!res.ok) return;

        const body = await res.json();
        setHtml((cache) => cacheHtml(cache, body.page));
        setProblems(body.errors ?? []);
      } catch {
        // A failed preview isn't worth interrupting writing over; the last good
        // render stays on screen.
      }
    }, 400);

    return () => clearTimeout(timer);
    // Keyed on the markdown alone. blocksJson is still what gets *sent*, so the
    // server always renders the current draft; it just isn't what decides that
    // a render is needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markdown, event.id]);

  // The draft: what the organizer has written. Deliberately carries no
  // page_published, so autosaving cannot change whether the page is live -
  // leaving it out means the server never assigns it.
  const draft = JSON.stringify({
    page_blocks: blocksJson,
    ...(customAllowed
      ? { custom_html: customHtml, custom_html_height: height }
      : {}),
  });

  // What is currently on the server. Seeded with what the page arrived holding
  // so an untouched editor never fires a pointless save.
  const saved = useRef<string | null>(null);
  if (saved.current === null) saved.current = draft;

  async function persist(body: string) {
    setStatus("saving");
    try {
      const res = await fetchWithCsrf(`/organize/${event.id}/page`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body,
      });
      if (!res.ok) throw new Error("save rejected");

      setStatus("saved");
      return true;
    } catch {
      // Left un-saved deliberately: the next edit retries, and the status line
      // says so rather than pretending the work is safe.
      setStatus("failed");
      return false;
    }
  }

  async function save() {
    if (draft === saved.current) return;

    const attempt = draft;
    if (await persist(attempt)) saved.current = attempt;
  }

  // Going live is an act, not a side effect of typing. A published page is
  // being read by attendees, and an organizer halfway through rewriting a
  // sentence should not be broadcasting it - so this is the only thing that
  // ever sends page_published, and it flushes the draft along with it.
  async function setLive(next: boolean) {
    const attempt = draft;
    const body = JSON.stringify({
      ...JSON.parse(attempt),
      page_published: next,
    });

    if (await persist(body)) {
      saved.current = attempt;
      setPublished(next);
    }
  }

  // Autosave. Long enough that it isn't saving mid-word, short enough that
  // closing the tab after a pause doesn't lose anything.
  useEffect(() => {
    const timer = setTimeout(save, 1200);
    return () => clearTimeout(timer);
    // Keyed on the draft so the timer re-arms when the page changes, and not
    // when something incidental like the save status re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  useEffect(() => {
    const pane = paneRef.current;
    if (!highlight || !pane) return;

    const target = pane.querySelector(`[data-slot="${highlight}"]`);
    if (!target) return;

    // The preview is CSS-scaled, so measured rects are what to trust here.
    // Nudging scrollTop keeps the movement inside the pane; scrollIntoView
    // would drag the whole editor page around it.
    const paneBox = pane.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    pane.scrollTo({
      top: pane.scrollTop + box.top - paneBox.top - paneBox.height / 3,
      behavior: "smooth",
    });
  }, [highlight]);

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow={event.name}
          title="Your event page"
          subtitle="What people see when you share your link."
          actions={
            <div className="flex flex-wrap gap-3">
              <ButtonLink href={`/organize/${event.id}`} variant="secondary">
                Dashboard
              </ButtonLink>
              <ButtonLink href={`/events/${event.id}`} variant="secondary">
                View live
              </ButtonLink>
            </div>
          }
        />

        {/* Two panes side by side once there's room; tabbed below that. */}
        <div className="flex gap-2 xl:hidden">
          {(["edit", "preview"] as const).map((tab) => (
            <Button
              key={tab}
              type="button"
              variant={mobileTab === tab ? "primary" : "secondary"}
              size="sm"
              onClick={() => setMobileTab(tab)}
            >
              {tab === "edit" ? "Edit" : "Preview"}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
          <div
            className={`flex flex-col gap-5 ${mobileTab === "edit" ? "" : "hidden xl:flex"}`}
          >
            <PageBlockEditor
              eventId={event.id}
              rows={rows}
              onChange={setRows}
              onActiveChange={(index) =>
                setHighlight(index === null ? null : slotFor(blocks, index))
              }
            />

            {customAllowed && (
              <Panel title="custom.html">
                <div className="flex flex-col gap-4">
                  <Field
                    label="Write your own HTML and CSS"
                    hint="It renders in a sandbox: styles and markup work, JavaScript doesn't run. Links open in a new tab."
                  >
                    <Textarea
                      rows={10}
                      value={customHtml}
                      onChange={(e) => setCustomHtml(e.target.value)}
                      className="font-mono"
                      placeholder="<style>…</style>&#10;<div>…</div>"
                      spellCheck={false}
                    />
                  </Field>

                  {/* Nothing runs inside the box, so it can't measure itself.
                      The slider is live against the preview so the height is a
                      thing you see rather than a number you guess. */}
                  <Field
                    label={`How tall: ${height}px`}
                    hint="Drag until your box fits."
                  >
                    <input
                      type="range"
                      min={height_range.min}
                      max={height_range.max}
                      step={20}
                      value={height}
                      onChange={(e) => setHeight(Number(e.target.value))}
                      className="accent-goldenrod w-full"
                    />
                  </Field>
                </div>
              </Panel>
            )}

            <Panel title="publish.txt">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <span className="font-display text-subheading font-bold">
                    {published
                      ? "This page is live"
                      : "This page isn't published yet"}
                  </span>
                  <span className="text-caption text-ink-soft">
                    {published
                      ? "Anyone with your link sees what you've written. Your edits save as you type, and go out when you press Update."
                      : "Your link still works - visitors just see the plain version until you publish."}
                  </span>
                </div>

                {/* Publishing is its own act. Typing saves the draft; nothing
                    an organizer writes reaches the public until they press
                    this, so a half-rewritten sentence is never live. */}
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    onClick={() => setLive(!published)}
                    variant={published ? "secondary" : "primary"}
                    disabled={status === "saving"}
                  >
                    {published ? "Unpublish" : "Publish this page"}
                  </Button>

                  {published && (
                    <Button
                      type="button"
                      onClick={() => setLive(true)}
                      disabled={status === "saving"}
                    >
                      Update the live page
                    </Button>
                  )}
                  <span
                    aria-live="polite"
                    className={
                      status === "failed"
                        ? "text-caption text-danger font-bold"
                        : "text-caption text-ink-soft"
                    }
                  >
                    {status === "saving"
                      ? "Saving…"
                      : status === "saved"
                        ? "Draft saved."
                        : status === "failed"
                          ? "Couldn't save - keep this tab open, it retries on your next edit."
                          : "Your draft saves as you write."}
                  </span>
                </div>
              </div>
            </Panel>
          </div>

          {/* Sticky, because the editor column gets much taller than the
              preview: without it you end up adjusting the height slider with
              the box it resizes scrolled off the screen. */}
          <div
            ref={paneRef}
            className={`flex max-h-[calc(100vh-2rem)] flex-col gap-5 overflow-y-auto xl:sticky xl:top-4 ${
              mobileTab === "preview" ? "" : "hidden xl:flex"
            }`}
          >
            {problems.length > 0 && (
              <Panel title="wont-show.txt" tone="alt">
                <ul className="flex list-disc flex-col gap-1 pl-4">
                  {problems.map((problem, i) => (
                    <li key={i} className="text-body text-ink-muted">
                      Your page {problem}.
                    </li>
                  ))}
                </ul>
              </Panel>
            )}

            {/* The page itself, at desktop width and scaled to fit. An empty
                page is still a real page - it falls back to the program's own
                copy - so this renders from the first visit rather than waiting
                for a section to be added. */}
            <PagePreviewFrame>
              <CityPage
                event={event}
                page={preview}
                myRsvp={null}
                isOrganizer={false}
                ageRange={age_range}
                preview
                highlight={highlight}
              />
            </PagePreviewFrame>
          </div>
        </div>
      </div>
    </Layout>
  );
}
